// ButtonGroup.tsx
import React, { useState, useEffect } from 'react';
import './ButtonGroup.css';

// Individual button configuration
interface ButtonOption {
  value: boolean;
  label: string;
  color: string;
}

// ButtonGroup component props
interface ButtonGroupProps {
  readonly options: readonly ButtonOption[];
  onChange?: (value?: boolean) => void;
  readonly defaultValue?: boolean;
  readonly disabled?: boolean;
  readonly size?: 'small' | 'medium' | 'large';
  readonly variant?: 'contained' | 'outlined';
}

const ButtonGroup: React.FC<ButtonGroupProps> = ({
  options,
  onChange,
  defaultValue,
  disabled = false,
  size = 'medium',
  variant = 'contained',
}) => {
  // Track currently selected button value
  const [selected, setSelected] = useState<boolean | undefined>(undefined);

  // Sync with defaultValue changes - clear selection when defaultValue is false/undefined
  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  // Handle button click - toggle selection
  const handleClick = (value: boolean) => {
    if (disabled) return; // Exit early if disabled or defaultValue is false
    const newValue = selected === value ? undefined : value; // Toggle: deselect if same, select if different
    setSelected(newValue); // Update local state
    onChange?.(newValue); // Notify parent component
  };

  return (
    <div className={`button-group ${size}`}>
      {options.map((option) => (
        <button
          key={option.label}
          onClick={() => handleClick(option.value)}
          className={`
            button
            ${size}
            ${variant}
            ${selected === option.value ? 'active' : ''}
            ${disabled ? 'disabled' : ''}
          `}
          style={
            {
              '--button-color': option.color,
            } as React.CSSProperties
          }
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

export default ButtonGroup;
